import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import type { RenderMode } from '@/types'
import * as THREE from 'three'
import { ImageSelectItemState } from '@/components/ImageSelect/index.vue'
import { generate3D, removeBackground } from '@/views/workspace/service'
import { useUserStore } from './user'
import { ModuleEnum } from '@/assets/artwork'
import { $bus } from '@/mitt'
import { useArtworkStore } from './artwork'
import { downloadFile, sleep } from '@/utils'
import { message } from 'ant-design-vue'
import { getClientId } from '@/hooks/useGenerate'
import { getGenerateParam } from '@/assets/artwork/module/threeTransform'
import { updateThreeDCollectStatus } from '@/views/workspace/hooks/common'
import { isLoginRequiredError } from '@/utils/requireLogin'
import { aegis } from "@/utils/monitoring";
import { useEventTracking } from '@/hooks/useEventTracking'
import {  Poller, PollResult } from '@/utils/QuickTaskPoller'
import { useDownloadImageStore } from './downloadImage'
import { TEAM_QUOTA_EXCEEDED_CODE } from '@/utils/teamQuotaWarning'

export enum WorkType {
    THREE_TRANSFORM = 'threeTransform',
    IMAGE = 'image',
    NONE = 'none',
    // 抠图中
    REMOVE_BACKGROUND = 'removeBackground',
    // 生成3D模型中
    GENERATE_MODEL = 'generateModel'
}

const DEFAULT_WORK_TYPE = WorkType.NONE;
const DEFAULT_THREE_D_SIMPLIFY = 0.9;
// 默认渲染模式
const DEFAULT_RENDER_MODE: RenderMode = 'baked';

// 默认环境光强度 10
export const DEFAULT_AMBIENT_LIGHT_INTENSITY = 10;
// 光源强度 1
export const DEFAULT_LIGHT_INTENSITY = 1;
// 背景颜色 #696969
export const DEFAULT_BACKGROUND_COLOR = '#696969';

// 添加轮询间隔常量
const POLLING_INTERVAL = 2000; // 2秒
const MODEL_POLLING_INTERVAL = 5000; // 5秒

/**
 * 抠图最大重试次数 
 */
const REMOVE_BACKGROUND_TRIES_MAX = 150;

interface Generate3DResponseData {
    imgId: number
    imgUrl: string
}

interface Generate3DResponse {
    code: number;
    data: Generate3DResponseData;
    status?: number; // 1-已完成, 2-生成中, -1-错误或取消
    msg?: string;
}

interface RemoveBackgroundResponseData {
    requestId: string;
    imgUrl: string;
    status: number;
}

interface RemoveBackgroundResponse {
    code: number;
    data: RemoveBackgroundResponseData;
    status?: number; // 1-已完成, 2-处理中, -1-错误或取消
    msg?: string;
}

interface ModelViewerState {
    /** 上传的图片url */
    imageUrl: string
    /** 去除背景后的图片url */
    removeBackgroundImageUrl: string
    /** 模型url */
    modelUrl: string
    /** 模型id */
    modelId: number
    /** 模型收藏状态 */
    modelCollect: number
    renderMode: RenderMode
    environmentMapIntensity: number
    lightIntensity: number
    backgroundColor: string
    isLoading: boolean
    showAxes: boolean
    showGrid: boolean
    modelInstance: any | null
    loadError: string | null
    ambientLight: THREE.AmbientLight | null
    workType: WorkType
    captureDataUrl: string
    needProcessImage: boolean
    isProcessing: boolean
    needHandleImage: boolean
    imageItem: ImageSelectItemState | null
    renderer: THREE.WebGLRenderer | null
    scene: THREE.Scene | null
    camera: THREE.Camera | null
    cameraPanelOpen: boolean
    currentBackgroundColor: string
    pendingBackgroundColor: string | null,
    isAutoRotating: boolean,
    isNeedResetViewer: boolean,
    isStartGenerate: boolean
    hasScreenshot: boolean
    isShowRetainModal: boolean
    isNeedShowRetainModal: boolean,
    isFinishEditHandler: boolean,
    isFinishRemoveBackground: boolean,
    modelViewerisOnMounted: boolean,
    /** 是否违规图片 */
    isViolationImage: boolean,
    /** 是否开始生成图片 */
    isStartPhotoTweet: boolean,
    /** 是否开始截图 */
    isStartTakeScreenshot: boolean,
    /** 3D模型简化程度 */
    threeDSimplify: number,
    /** 3D生成参数 */
    threeDGenerateParams: Generate3DParams | null,
    /** 是否生成3D模型完成 */
    isGenerateThreeDFinish: boolean,
    /** 是否离开了创作模块 */
    isLeaveWorkspace: boolean,
    /** 是否显示请先去除图像背景 */
    isShowRemoveBackgroundTip: boolean,
    /** 是否需要去除背景 */
    isNeedRemoveBackground: boolean,
    /** 是否显示下载弹窗 */
    isShowDownloadPopover: boolean,
}

// 新增类型定义
interface Generate3DParams {
    userId: string;
    module: ModuleEnum.generateThreeTransform;
    threeDOriginImgUrl: string;
    threeDRmbgImg: string;
    threeDSimplify: number;
    threeDClientId: string;
    artworkId: number;
    source: number;
}

// 提取常量
const GENERATE_3D = {
    MODULE_ID: 11,
    SOURCE: 1,
    MAX_RETRIES: 90,
    STATUS: {
        FAILED: -1,
        PROCESSING: 2,
        COMPLETED: 3
    }
} as const;

export const useModelViewerStore = defineStore('modelViewer', {
    state: (): ModelViewerState => ({
        // 上传的图片url
        imageUrl: '',
        // 模型url
        modelUrl: '',
        // 模型id
        modelId: 0,
        /** 模型收藏状态 */
        modelCollect: 0,
        // 抠图后的图片url
        removeBackgroundImageUrl: '',
        // 工作台当前类型
        workType: DEFAULT_WORK_TYPE,
        // 是否需要处理图片
        needProcessImage: false,
        // 是否正在处理图片
        isProcessing: false,
        // 截图的dataUrl
        captureDataUrl: '',
        imageItem: null,
        needHandleImage: false,
        renderMode: DEFAULT_RENDER_MODE,
        /** 环境贴图强度 */
        environmentMapIntensity: DEFAULT_AMBIENT_LIGHT_INTENSITY,
        lightIntensity: DEFAULT_LIGHT_INTENSITY, // 光源强度
        backgroundColor: DEFAULT_BACKGROUND_COLOR,
        isLoading: false,
        showAxes: false,
        showGrid: false,
        modelInstance: null,
        loadError: null,
        ambientLight: null,
        renderer: null,
        scene: null,
        camera: null,
        cameraPanelOpen: false,
        currentBackgroundColor: DEFAULT_BACKGROUND_COLOR,
        pendingBackgroundColor: null,
        // 是否自动旋转
        isAutoRotating: false,
        isNeedResetViewer: false,
        // 是否开始生成效果图
        isStartGenerate: false,
        hasScreenshot: false,
        isNeedShowRetainModal: false,
        // 退出时是否保存
        isShowRetainModal: false,
        isFinishEditHandler: false,
        // 是否已完成背景处理
        isFinishRemoveBackground: false,
        modelViewerisOnMounted: false,
        // 是否违规图片
        isViolationImage: false,
        isStartPhotoTweet: false,
        isStartTakeScreenshot: false,
        threeDSimplify: DEFAULT_THREE_D_SIMPLIFY,
        threeDGenerateParams: null,
        isGenerateThreeDFinish: true,
        isLeaveWorkspace: false,
        isShowRemoveBackgroundTip: false,
        isNeedRemoveBackground: false,
        isShowDownloadPopover: false,
    }),

    actions: {
        /* 删除模型 */
        deleteModel() {
            this.modelInstance = null;
            this.scene = null;
            this.camera = null;
            this.renderer = null;
        },
        /** 加载模型 */
        async loadModel(url: string) {
            this.isLoading = true
            this.loadError = null

            try {
                this.modelUrl = url
                this.resetViewerSettings()

                const loader = await this.getModelLoader(url)
                const loadedModel = await loader.loadAsync(url)

                const extension = url.split('.').pop()?.toLowerCase()
                if (extension === 'gltf' || extension === 'glb') {
                    this.modelInstance = markRaw(loadedModel.scene)
                } else {
                    this.modelInstance = markRaw(loadedModel)
                }

                window.dispatchEvent(new CustomEvent('model-loaded', {
                    detail: { model: this.modelInstance }
                }))

            } catch (error) {
                console.error('Failed to load model:', error)
                this.loadError = error instanceof Error ? error.message : '模型加载失败'
                throw error
            } finally {
                this.isLoading = false
            }
        },


        async getModelLoader(url: string) {
            const extension = url.split('.').pop()?.toLowerCase()

            switch (extension) {
                case 'gltf':
                case 'glb':
                    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader')
                    return new GLTFLoader()
                case 'fbx':
                    const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader')
                    return new FBXLoader()
                default:
                    throw new Error(`不支持的文件格式: ${extension}`)
            }
        },

        updateRenderMode(mode: RenderMode) {
            this.renderMode = mode
        },

        updateLightSettings(params: Partial<ModelViewerState>) {
            Object.assign(this, params)
            if (this.ambientLight) {
                this.ambientLight.intensity = this.lightIntensity
            }
        },

        toggleHelper(type: 'axes' | 'grid', value: boolean) {
            if (type === 'axes') {
                this.showAxes = value
            } else {
                this.showGrid = value
            }
        },

        updateBackgroundColor(color: string) {
            this.currentBackgroundColor = color;
            this.pendingBackgroundColor = color;

            if (this.renderer) {
                this.renderer.setClearColor(new THREE.Color(color));
                this.pendingBackgroundColor = null;
            }
        },

        resetViewerSettings() {
            this.renderMode = 'baked'
            this.environmentMapIntensity = DEFAULT_AMBIENT_LIGHT_INTENSITY
            this.lightIntensity = DEFAULT_LIGHT_INTENSITY
            this.showAxes = false
            this.showGrid = false
            this.backgroundColor = DEFAULT_BACKGROUND_COLOR
        },
        resetModelData() {
            this.imageUrl = ''
            this.modelUrl = ''
            this.removeBackgroundImageUrl = ''
            this.isNeedShowRetainModal = false;
            this.workType = WorkType.NONE
            this.hasScreenshot = false;
            this.isFinishRemoveBackground = false;
            this.backgroundColor = DEFAULT_BACKGROUND_COLOR;
            this.currentBackgroundColor = DEFAULT_BACKGROUND_COLOR;
            this.isViolationImage = false;

            this.modelId = 0;
            this.modelCollect = 0;

            const artworkStore = useArtworkStore();
            artworkStore.threeTransform.params.layoutRefer = null;
            artworkStore.threeTransform.params.imgTags = [];
            // artworkStore.threeTransform.result = {};
        },
        // 删除图片和模型
        deleteImageAndModel() {
            this.resetModelData();
            // 背景颜色设置为白色
            this.backgroundColor = DEFAULT_BACKGROUND_COLOR;
            this.currentBackgroundColor = DEFAULT_BACKGROUND_COLOR;
            this.isFinishRemoveBackground = false;
            this.isViolationImage = false;

            // 清空结果
            const artworkStore = useArtworkStore();
            artworkStore.threeTransform.result = {};

            // 重置请先去除图像背景
            this.isShowRemoveBackgroundTip = false;
            this.isNeedRemoveBackground = false;

            // 收起参数面板
            $bus.emit('artworkDrawerCollapse', true)

        },
        async mockGenerateModel() {
            message.info('模拟生成模型中...')
            await sleep(1000);
            this.modelUrl = 'https://pino-img.yingzhongshare.com/20241217_10055420241217_1005171734401113817-4map.glb'
            this.workType = WorkType.THREE_TRANSFORM
            this.isNeedShowRetainModal = true;
            $bus.emit('artworkDrawerCollapse', {
                value: false,
                target: 'params'
            })
        },
        // 去掉背景
        async removeBackground() {
            useEventTracking({
                event: 'click',
                eventValue: {
                    button: '3D_remove_bg'
                }
            });
            this.isNeedRemoveBackground = false;
            this.isShowRemoveBackgroundTip = false;
            if (this.isFinishRemoveBackground || this.isViolationImage) {
                return;
            }

            const startTime = Date.now();

            this.workType = WorkType.REMOVE_BACKGROUND;
            const userStore = useUserStore();

            try {
                const threeDClientId = getClientId();

                // 初始请求获取requestId
                try {
                    const initRes: RemoveBackgroundResponse = await removeBackground({
                        userId: userStore.userInfo.id,
                        module: 10,
                        threeDOriginImgUrl: this.imageUrl,
                        threeDClientId
                    });

                    if (initRes.code === 0) {
                    } else {
                        throw new Error(initRes.msg || '初始化背景移除失败');
                    }
                } catch (error: unknown) {
                    if ((error as { code?: number }).code === 10000) {
                        this.imageUrl = (error as { data: string }).data;
                        this.isViolationImage = true;
                    } else if ((error as { code?: number }).code === TEAM_QUOTA_EXCEEDED_CODE) {
                        // 90711 已由 responseCodeHandle 弹窗，不再重复 toast
                    } else {
                        message.error((error as Error).message || '背景移除失败');
                    }
                    this.workType = WorkType.IMAGE;
                    return;
                }

                // 开始轮询检查状态
                let retryCount = 0;

                //  || retryCount < REMOVE_BACKGROUND_TRIES_MAX
                while (true && !this.isLeaveWorkspace) {
                    await sleep(POLLING_INTERVAL);
                    retryCount++;

                    try {
                        const pollRes: RemoveBackgroundResponse = await removeBackground({
                            userId: userStore.userInfo.id,
                            module: 10,
                            threeDOriginImgUrl: this.imageUrl,
                            threeDClientId
                        });

                        if (pollRes.code === 0) {
                            switch (pollRes.data.status) {
                                case 2: // 继续轮询
                                    continue;
                                case 3: // 处理成功
                                    this.removeBackgroundImageUrl = pollRes.data.imgUrl;
                                    // this.imageUrl = pollRes.data.imgUrl;
                                    this.workType = WorkType.IMAGE;
                                    this.isFinishRemoveBackground = true;

                                    aegis?.infoAll({
                                        msg: '耗时计算-3D转换-背景移除成功',
                                        /** 总耗时 */
                                        ext1: `${((Date.now() - startTime) / 1000).toFixed(2)}s`,
                                    });

                                    userStore.queryUserRights();
                                    return;

                                case -1: // 处理失败
                                    throw new Error(pollRes.msg || '背景移除失败');
                                default:
                                    throw new Error('未知的处理状态');
                            }
                        } else {
                            throw new Error(pollRes.msg || '查询处理状态失败');
                        }
                    } catch (error: unknown) {
                        if ((error as { code?: number }).code === 10000) {
                            this.imageUrl = (error as { data: string }).data;
                            this.isViolationImage = true;
                        } else if ((error as { code?: number }).code === TEAM_QUOTA_EXCEEDED_CODE) {
                            // 90711 已由 responseCodeHandle 弹窗，不再重复 toast
                        } else {
                            message.error((error as Error).message || '背景移除失败');
                        }
                        this.workType = WorkType.IMAGE;
                        return;
                    }
                }

                if (!this.isLeaveWorkspace) {
                    // 超出最大重试次数
                    message.error('背景移除超时，请重试');
                    this.workType = WorkType.IMAGE;
                }

            } catch (error) {
                console.error('背景移除过程发生错误:', error);
                this.workType = WorkType.IMAGE;
            }
        },

        // 初始化3D生成请求
        async initGenerate3DRequest(params: Generate3DParams): Promise<void> {
            const initRes = await generate3D(params);
            if (initRes.code !== 0) {
                throw new Error(initRes.msg || '初始化3D生成失败');
            }
        },

        // 轮询3D生成状态
        async poll3DGenerateStatus(params: Generate3DParams): Promise<Generate3DResponse> {
            const pollRes = await generate3D(params);
            if (pollRes.code !== 0) {
                throw new Error(pollRes.msg || '查询生成状态失败');
            }
            return pollRes;
        },

        // 处理生成完成
        handleGenerateSuccess(modelUrl: string, modelId: number): void {
            this.modelUrl = modelUrl;
            this.workType = WorkType.THREE_TRANSFORM;
            this.isNeedShowRetainModal = true;
            this.modelId = modelId;
            this.modelCollect = 0;

            $bus.emit('artworkDrawerCollapse', {
                value: false,
                target: 'params'
            });

            useUserStore().queryUserRights();
            $bus.emit('hasNewVersion');
            this.isGenerateThreeDFinish = true;
        },

        // 处理生成错误
        handleGenerateError(error: unknown): void {
            const errorObj = error as { code?: number; data?: string; message?: string };

            if (errorObj.code === 10000) {
                this.imageUrl = errorObj.data || '';
                this.isViolationImage = true;
            } else if (errorObj.code === TEAM_QUOTA_EXCEEDED_CODE) {
                // 90711 已由 responseCodeHandle 弹窗，不再重复 toast
            } else {
                message.error(errorObj.message || '3D模型生成失败');
            }

            this.workType = WorkType.IMAGE;
        },

        async generateModel() {
            if (this.isViolationImage) return;

            useEventTracking({
                event: 'click',
                eventValue: {
                    button: '3D_transform'
                }
            });

            const userStore = useUserStore();
            const artworkStore = useArtworkStore();

            // 检查用户权限
            if (!userStore.usePowerPackageModal() && !artworkStore.artworkState.id) {
                await artworkStore.startGenerate(ModuleEnum.threeTransform);
            }

            const startTime = Date.now();
            this.workType = WorkType.GENERATE_MODEL;

            let artworkParams = await getGenerateParam()

            try {
                const threeDClientId = getClientId();
                const lastParams = this.threeDGenerateParams;
                let requestParams: Generate3DParams;
                if (!this.isGenerateThreeDFinish) {
                    requestParams = lastParams;
                } else {
                    requestParams = {
                        ...artworkParams,
                        userId: userStore.userInfo.id,
                        module: GENERATE_3D.MODULE_ID,
                        threeDOriginImgUrl: this.imageUrl,
                        threeDRmbgImg: this.removeBackgroundImageUrl || this.imageUrl,
                        threeDSimplify: this.threeDSimplify,
                        threeDClientId,
                        artworkId: artworkStore.artworkState.id,
                        source: GENERATE_3D.SOURCE,
                    };
                }

                this.threeDGenerateParams = requestParams;
                this.isGenerateThreeDFinish = false;

                artworkStore.generateThreeTransform.params = requestParams;

                // 初始化请求
                await this.initGenerate3DRequest(requestParams);

                // 轮询状态
                // for (let retryCount = 0; retryCount < GENERATE_3D.MAX_RETRIES; retryCount++) {
                //     await sleep(MODEL_POLLING_INTERVAL);

                //     const pollRes = await this.poll3DGenerateStatus(requestParams);

                //     switch (pollRes.data.status) {
                //         case GENERATE_3D.STATUS.FAILED:
                //             throw new Error(pollRes.msg || '3D模型生成失败');
                //         case GENERATE_3D.STATUS.PROCESSING:
                //             continue;
                //         case GENERATE_3D.STATUS.COMPLETED:
                //             let { imgUrl, imgId } = pollRes.data;
                //             this.handleGenerateSuccess(imgUrl, imgId);
                //             return;
                //         default:
                //             throw new Error('未知的生成状态');
                //     }
                // }


                while (true && !this.isLeaveWorkspace) {
                    await sleep(MODEL_POLLING_INTERVAL);

                    const pollRes = await this.poll3DGenerateStatus(requestParams);

                    switch (pollRes.data.status) {
                        case GENERATE_3D.STATUS.FAILED:
                            throw new Error(pollRes.msg || '3D模型生成失败');
                        case GENERATE_3D.STATUS.PROCESSING:
                            continue;
                        case GENERATE_3D.STATUS.COMPLETED:
                            let { imgUrl, imgId } = pollRes.data;
                            this.handleGenerateSuccess(imgUrl, imgId);

                            aegis?.infoAll({
                                msg: '耗时计算-3D转换-模型生成成功',
                                /** 总耗时 */
                                ext1: `${((Date.now() - startTime) / 1000).toFixed(2)}s`,
                            });
                            return;
                        default:
                            throw new Error('未知的生成状态');
                    }
                }

                // throw new Error('3D模型生成超时');

            } catch (error) {
                this.handleGenerateError(error);
            } finally {
                const totalTime = (Date.now() - startTime) / 1000;
            }
        },
        // 下载glb模型文件到本地
        /**
         * 下载3D模型文件到本地
         * @param url 模型文件URL
         * @returns void
         */
        downloadModel(url: string): void {
            if (!url?.trim()) {
                message.error('模型文件不存在');
                return;
            }

            try {
                const fileName = url.split('/').pop() || 'model.glb';
                downloadFile(url, fileName);
                // const fileName = url.split('/').pop() || 'model.glb';
                // const downloadLink = document.createElement('a');
                // downloadLink.href = url;
                // downloadLink.download = fileName;
                // downloadLink.style.display = 'none';

                // document.body.appendChild(downloadLink);
                // downloadLink.click();
                // document.body.removeChild(downloadLink);
            } catch (error) {
                message.error('下载模型失败,请重试');
            }
        },
        // 收藏模型
        async favoriteModel({ id, collect }: { id: number, collect: number }) {
            const nextStatus = collect === 1 ? 0 : 1;
            try {
                await updateThreeDCollectStatus({
                    id,
                    collect
                });
            } catch (error) {
                if (isLoginRequiredError(error)) return;
                throw error;
            }

            this.modelCollect = nextStatus;

            $bus.emit('modelCollectStatusUpdate', {
                status: nextStatus,
                id
            });

        },
        setCaptureDataUrl(url: string) {
            this.captureDataUrl = url
            this.needProcessImage = true
        },
        // 重置截图状态
        resetCaptureDataState() {
            this.captureDataUrl = ''
            this.needProcessImage = false
        },
        // 添加设置三维场景相关组件的方法
        setSceneComponents(
            renderer: THREE.WebGLRenderer,
            scene: THREE.Scene,
            camera: THREE.Camera
        ) {
            this.renderer = markRaw(renderer)
            this.scene = markRaw(scene)
            this.camera = markRaw(camera)

            if (this.pendingBackgroundColor) {
                this.renderer.setClearColor(new THREE.Color(this.pendingBackgroundColor));
                this.pendingBackgroundColor = null;
            }
        },
        // 截图方法
        async takeScreenshot(options = { transparent: true }): Promise<string> {
            return new Promise((resolve, reject) => {
                try {
                    if (!this.renderer || !this.scene || !this.camera) {
                        reject(new Error('三维场景组件未初始化'));
                        return;
                    }

                    if (this.isStartTakeScreenshot) {
                        reject(new Error('截图进行中'));
                        return;
                    }

                    useEventTracking({
                        event: 'click',
                        eventValue: {
                            button: '3D_screenshot'
                        }
                    });

                    this.isStartTakeScreenshot = true;

                    // 保存原始设置
                    const originalRatio = this.renderer.getPixelRatio()
                    const originalClear = this.renderer.autoClear
                    const originalSize = {
                        width: this.renderer.domElement.width,
                        height: this.renderer.domElement.height
                    }
                    const originalAspect = (this.camera as THREE.PerspectiveCamera).aspect

                    // 设置固定的渲染尺寸为1024*1024
                    const width = 1024
                    const height = 1024
                    this.renderer.setSize(width, height, false)
                    this.renderer.setPixelRatio(1)
                    this.renderer.autoClear = true

                    // 更新相机宽高比
                    if (this.camera instanceof THREE.PerspectiveCamera) {
                        this.camera.aspect = width / height
                        this.camera.updateProjectionMatrix()
                    }

                    // 渲染图像
                    this.renderer.render(this.scene, this.camera)
                    const dataURL = this.renderer.domElement.toDataURL('image/png')

                    // 恢复原始设置
                    this.renderer.setSize(originalSize.width, originalSize.height, false)
                    this.renderer.setPixelRatio(originalRatio)
                    this.renderer.autoClear = originalClear

                    // 恢复相机原始宽高比
                    if (this.camera instanceof THREE.PerspectiveCamera) {
                        this.camera.aspect = originalAspect
                        this.camera.updateProjectionMatrix()
                    }

                    // 设置截图数据
                    this.setCaptureDataUrl(dataURL)
                    this.hasScreenshot = true
                    this.isStartTakeScreenshot = false;

                    resolve(dataURL);
                } catch (error) {
                    this.isStartTakeScreenshot = false;
                    reject(error);
                }
            });
        },
        // 再次生成3D模型
        async againGenerateModel() {
            useEventTracking({
                event: 'click',
                eventValue: {
                    button: '3D_retransform'
                }
            });

            this.workType = WorkType.GENERATE_MODEL
            const artworkStore = useArtworkStore();
            if (artworkStore.generateThreeTransform.params.threeDClientId) {
                artworkStore.generateThreeTransform.params.threeDClientId = getClientId();
            }
            await this.generateModel()
        },
        // 生成图片
        async generateImage() {
            useEventTracking({
                event: 'click',
                eventValue: {
                    button: '3D_generate'
                }
            });

            this.isStartGenerate = true;

            this.isNeedShowRetainModal = false;
        },
        superModelGenerate(params: any) {
            return generate3D(params)
        },
        /**
         * 高清模型模型生成
         */
        async highDefinitionModelGenerate(params: {
            // 原图
            threeDOriginImgUrl: string,
            // 去掉背景后的图片
            threeDRmbgImg: string,
            threeDClientId: string
        }): Promise<{
            code: number;
            msg: string;
            data: {
                imgUrl: string;
                status: number;
            }
        }> {
            const poller = new Poller({
                shouldLog: true,
                shouldPrint: true,
                logMessage: `耗时计算-高清模型模型生成`,
                loop: true
            });

            const downloadImageStore = useDownloadImageStore();
            // 是否需要更新算力
            let emitPowerUpdated = false;
            const userStore = useUserStore();
            downloadImageStore.updateDownload('highDefinitionModelGenerating');

            Object.assign(params, {
                module: 17,
                userId: userStore.userInfo.id,
                // threeDTextureSize: 2048,
                // threeDSimplify: 0.2,
                threeDTextureSize: 1024,
                threeDSimplify: 0.8,
                threeDPostProcess: "true"
            });


            try {
                const finalResult = await poller
                    .on('complete', async (result: any) => {
                        return result;
                    })
                    .on('error', (error: any) => {
                        console.error('高清模型模型生成:', error);
                        userStore.queryUserRights();
                        downloadImageStore.updateDownload('highDefinitionModelGenerateError');
                        throw error;
                    })
                    .on('poll', () => {
                        if (!emitPowerUpdated) {
                            emitPowerUpdated = true;
                            // 通知更新算力
                            userStore.queryUserRights();
                        }
                    })
                    .poll(
                        () => this.superModelGenerate(params),
                        (result: any) => {
                            switch (result.data.status) {
                                case GENERATE_3D.STATUS.FAILED:
                                    throw new Error(result.msg || '3D模型生成失败');
                                case GENERATE_3D.STATUS.PROCESSING:
                                    return PollResult.CONTINUE;
                                case GENERATE_3D.STATUS.COMPLETED:
                                    return PollResult.COMPLETE;
                                default:
                                    throw new Error('未知的生成状态');
                            }
                        }
                    );

                return finalResult;
            } catch (error) {
                console.error('轮询失败:', error);
                throw error;
            }
        },
        // 模拟生成
        simulateGenerate() {
            this.workType = WorkType.THREE_TRANSFORM
            this.modelUrl = 'https://pino-assets.oss-cn-shanghai.aliyuncs.com/projects/pino/test/1725963374359/assets/dog_and_girl.glb';
        },
        toggleDrawerCollapse(isOpen: boolean) {
            $bus.emit('artworkDrawerCollapse', isOpen)
        },
        updateViewerSize(width: number, height: number) {
            if (this.renderer && this.camera) {

                // 更新渲染器尺寸和像素比
                this.renderer.setSize(width, height, true) // 设置为true以更新canvas元素尺寸
                this.renderer.setPixelRatio(window.devicePixelRatio)

                // 更新相机宽高比
                if (this.camera instanceof THREE.PerspectiveCamera) {
                    this.camera.aspect = width / height
                    this.camera.updateProjectionMatrix()
                }

                // 更新场景和控制器
                if (this.scene && this.controls) {
                    this.controls.update() // 更新控制器
                    this.renderer.render(this.scene, this.camera)
                }

                // 强制重新渲染一次
                requestAnimationFrame(() => {
                    if (this.scene && this.camera) {
                        this.renderer?.render(this.scene, this.camera)
                    }
                })
            }
        }
    },

    getters: {
    },
    persist: {
        key: 'pinioModelViewerStore',
        storage: window.localStorage,
        paths: ['threeDGenerateParams', 'isGenerateThreeDFinish']
    },
}) 